import { useSelectedRowSnapshotsStore } from "@/components/query-table/stores/selectedRowSnapshotsStore";
import { useRowSelectionContext } from "@/components/data-table/context/RowSelectionContext";
import { useMutationWithErrorHandling } from "@/hooks/useMutationWithErrorHandling";
import { ContractorsPlannedHoursWizardService } from "@/services/contractorsPlannedHoursWizardService";
import { ApiError } from "@/services/api";
import {
  CalculateContractorsPlannedHoursWizardRequest,
  DisplayContractorsPlannedHoursWizardAllocation,
  ReadContractorsPlannedHoursWizardResult,
} from "@/types/contractorsPlannedHoursWizard";
import { useCallback, useState, useSyncExternalStore } from "react";

export type ContractorsPlannedHoursWizardTabId = "selecting" | "results";

export function useContractorsPlannedHoursWizardTabs() {
  const [calculationResults, setCalculationResults] = useState<
    ReadContractorsPlannedHoursWizardResult[]
  >([]);
  const [calculationSnapshot, setCalculationSnapshot] = useState<
    DisplayContractorsPlannedHoursWizardAllocation[]
  >([]);
  const [activeTab, setActiveTab] =
    useState<ContractorsPlannedHoursWizardTabId>("selecting");

  const calculateMutation = useMutationWithErrorHandling<
    ReadContractorsPlannedHoursWizardResult[],
    ApiError,
    CalculateContractorsPlannedHoursWizardRequest,
    unknown
  >({
    mutationFn: ContractorsPlannedHoursWizardService.calculate,
    onSuccess: (data) => {
      setCalculationResults(data);
      setActiveTab("results");
    },
  });

  const { store } = useRowSelectionContext();
  useSyncExternalStore(
    store.subscribe,
    () => store.getSelectionCount(),
    () => 0,
  );
  const selectedIds = [...store.getSelection()];

  const handleCalculate = useCallback(() => {
    const uniqueIds = [...new Set(selectedIds)];
    if (uniqueIds.length === 0) {
      return;
    }
    const rows = useSelectedRowSnapshotsStore
      .getState()
      .getSnapshotRowsForIds(
        "contractorsPlannedHoursWizardAllocations",
        uniqueIds,
      ) as DisplayContractorsPlannedHoursWizardAllocation[];
    setCalculationSnapshot(rows.map((r) => ({ ...r })));
    calculateMutation.mutate({ schedules: uniqueIds });
  }, [selectedIds, calculateMutation]);

  const calculateDisabled =
    selectedIds.length === 0 || calculateMutation.isPending;

  return {
    activeTab,
    setActiveTab,
    calculationResults,
    calculationSnapshot,
    calculateMutation,
    handleCalculate,
    calculateDisabled,
  };
}
